import {createAction, createReducer} from '@reduxjs/toolkit';
import {get} from 'lodash-es';
import {DEFAULT_ORDER_FORM_DATA, DEFAULT_SIP_FORM_DATA} from '../../../constants/equity/orderForm/oneTime';

const MODULE_PREFIX = '[ ORDER_FORM ]';

const Actions = {
	setOneTimeData: `${MODULE_PREFIX} setOneTimeData`,
	resetOneTimeData: `${MODULE_PREFIX} resetOneTimeData`,
	setSipData: `${MODULE_PREFIX} setSipData`,
	resetSipData: `${MODULE_PREFIX} resetSipData`,
	fetchOrderScripData: `${MODULE_PREFIX} fetchOrderScripData`,
	getOneTimeError: `${MODULE_PREFIX} getOneTimeError`,
	setOneTimeError: `${MODULE_PREFIX} setOneTimeError`,
	getSipError: `${MODULE_PREFIX} getSipError`,
	setSipError: `${MODULE_PREFIX} setSipError`,
	modifyOneTimeOrder: `${MODULE_PREFIX} modifyOneTimeOrder`,
    updateOneTimeOrder: `${MODULE_PREFIX} updateOneTimeOrder`,
	updateSipOrder: `${MODULE_PREFIX} updateSipOrder`,
    fetchMarginInfo: `${MODULE_PREFIX} fetchMarginInfo`,
	setMarginInfo: `${MODULE_PREFIX} setMarginInfo`,
	setPaymentData: `${MODULE_PREFIX} setPaymentData`,
	setFormErrorDate: `${MODULE_PREFIX} setFormErrorDate`,
	resetFormErrorDate: `${MODULE_PREFIX} resetFormErrorDate`,

	fetchBrokerageCharges: `${MODULE_PREFIX} fetchBrokerageCharges`,
};

// actions
export const fetchOrderScripData = createAction(Actions.fetchOrderScripData);
export const setOneTimeData = createAction(Actions.setOneTimeData);
export const resetOneTimeData = createAction(Actions.resetOneTimeData);
export const setSipData = createAction(Actions.setSipData);
export const resetSipData = createAction(Actions.resetSipData);
export const modifyOneTimeOrder = createAction(Actions.modifyOneTimeOrder);
export const updateOneTimeOrder = createAction(Actions.updateOneTimeOrder);
export const updateSipOrder = createAction(Actions.updateSipOrder);
export const fetchMarginInfo = createAction(Actions.fetchMarginInfo);
export const setMarginInfo = createAction(Actions.setMarginInfo);
export const getOneTimeError = createAction(Actions.getOneTimeError);
export const setOneTimeError = createAction(Actions.setOneTimeError);
export const getSipError = createAction(Actions.getSipError);
export const setSipError = createAction(Actions.setSipError);
export const setPaymentData = createAction(Actions.setPaymentData);
export const setFormErrorDate = createAction(Actions.setFormErrorDate);
export const resetFormErrorDate = createAction(Actions.resetFormErrorDate);
export const fetchBrokerageCharges = createAction(Actions.fetchBrokerageCharges);

// selector
export const getOneTimeOrderFormData = (state) => state.orderForm.oneTimeData;
export const getSipOrderFormData = (state) => state.orderForm.sipData;
export const getOneTimeOrderQuantity = (state) => {
	const quantity = get(state, 'orderForm.oneTimeData.quantity', '');
	return quantity;
};

export const getOneTimeOrderType = (state) => {
	return get(state, 'orderForm.oneTimeData.orderType', '');
};

export const getOneTimeIsAmo = (state) => {
	return get(state, 'orderForm.oneTimeData.isAmo', false);
};

export const getOneTimeOrderValidity = (state) => {
	const validity = get(state, 'orderForm.oneTimeData.validity', '');
	return validity;
};

export const getOneTimeErrorData = (state) => {
	const isError = get(state, 'orderForm.oneTimeError.isError', false);
	const errorMessage = get(state, 'orderForm.oneTimeError.errorMessage', '');
	return { isError, errorMessage };
};

export const getSipErrorData = (state) => {
	const isError = get(state, 'orderForm.sipError.isError', false);
	const errorMessage = get(state, 'orderForm.sipError.errorMessage', '');
	return { isError, errorMessage };
};

export const getOrderMarginInfo = (state) => {
	const marginInfo = get(state, 'orderForm.marginInfo', '');
	return marginInfo;
};

export const checkOneTimeOrderFieldError = (state, field) => {
	const errorField = get(state, 'orderForm.oneTimeError.field', '');
	return errorField === field;
};

export const checkSipOrderFieldError = (state, field) => {
	const errorField = get(state, 'orderForm.sipError.field', '');
	return errorField === field;
};

export const getEditOrderType = (state) => {
	const editOrderList = get(state, 'orderForm.editOrderData.type', '');
	return editOrderList;
};
export const getEditOrderFormData = (state) => {
	const editOrderList = get(state, 'orderForm.editOrderData.formData', {});
	return editOrderList;
};
export const getEditOrderSipId = (state) => {
	const editOrderList = get(state, 'orderForm.editOrderData.sipId', '');
	return editOrderList;
};

export const getIsEditOrderModify = (state) => {
	const editOrderList = get(state, 'orderForm.editOrderData.isModify', false);
	return editOrderList;
};

export const getIsEditReOrder = (state) => {
	const editOrderList = get(state, 'orderForm.editOrderData.isReOrder', false);
	return editOrderList;
};

export const getWave2PaymentData = (state) => {
	const paymentData = get(state, 'orderForm.paymentData', {});
	return paymentData;
};

export const getFormErrorDate = (state) => {
	return get(state, 'orderForm.formErrorDate', {});
};

export const getPriceRange = (state) => state.orderForm.priceRange;

export const getOrderTypeValue = (state) => {
    const orderType = get(
        state,
        'orderForm.oneTimeData.orderType',
        DEFAULT_ORDER_FORM_DATA.orderType,
    );
    return orderType;
};

export const getOneTimeOrderPrice = (state) => {
	const price = get(
		state,
		'orderForm.oneTimeData.price',
		DEFAULT_ORDER_FORM_DATA.price,
	);
	return price;
};

const initialErrorData = {
	isError: false,
	errorMessage: '',
	field: '',
};

const initialState = {
	oneTimeData: DEFAULT_ORDER_FORM_DATA,
	sipData: DEFAULT_SIP_FORM_DATA,
	oneTimeError: initialErrorData,
	sipError: initialErrorData,
	multilegData: [],
	marginInfo: {},
	paymentData: {},
	editOrderData: '',
	priceRange: {},
	deliveryData: {},
	intradayData: {},
	deliveryPlusData: {},
	mtfData: {},
	carryForwardData: {},
	formErrorDate: {
		errorType: '',
		isVisible: false,
		apiError: '',
	},
};

// reducers
export default createReducer(initialState, (builder) => {
	builder.addCase(setMarginInfo, (state, action) => {
		state.marginInfo = action.payload;
	});
	builder.addCase(setOneTimeData, (state, action) => {
		const { payload } = action;
		if (state.oneTimeError.isError) {
			state.oneTimeError = initialErrorData;
		}
		state.oneTimeData = { ...state.oneTimeData, ...payload };
	});
	builder.addCase(resetOneTimeData, (state, action) => {
		const { payload = DEFAULT_ORDER_FORM_DATA } = action;
		state.oneTimeData = payload;
	});
	builder.addCase(setSipData, (state, action) => {
		const { payload } = action;
		if (state.sipError.isError) {
			state.sipError = initialErrorData;
		}
		state.sipData = { ...state.sipData, ...payload };
	});
	builder.addCase(resetSipData, (state, action) => {
		const { payload = DEFAULT_SIP_FORM_DATA } = action;
		state.sipData = payload;
	});
	builder.addCase(setOneTimeError, (state, action) => {
		state.oneTimeError = action.payload;
	});
	builder.addCase(setSipError, (state, action) => {
		state.sipError = action.payload;
	});
	builder.addCase(setPaymentData, (state, action) => {
		state.paymentData = { ...state.paymentData, ...action.payload };
	});
	builder.addCase(setFormErrorDate, (state, action) => {
		state.formErrorDate = {...state.formErrorDate, ...action.payload};
	});
	builder.addCase(resetFormErrorDate, (state) => {
		state.formErrorDate = {};
	});
});
